import React, { FC, Fragment } from 'react';
import './CustomButton.scss';
import { objectToClassString } from '../../utils/functions';
import { Button, Spinner } from '@chakra-ui/react';
import classNames from 'classnames';
import { PlusIcon } from '@phosphor-icons/react/dist/ssr';
import Typhography from '../Typhography/Typhography';

interface ICustomButton {
  color?: 'primary' | 'secondary' | 'outline' | 'text' | 'error';
  iconPosition?: 'left' | 'right';
  outline?: boolean;
  disabled?: boolean;
  text?: boolean;
  textSmall?: boolean;
  fullWidth?: boolean;
  /**
   * Dimensione bottone:
   * - "md" (default): padding 14.5x24, min-height 44, font 14, radius 16
   * - "sm": padding 6x12, min-height 32, font 12, radius 10 (toolbar/inline actions)
   * - "xs": padding 4x10, min-height 26, font 11, radius 8 (chip-like)
   */
  size?: 'xs' | 'sm' | 'md';
  onClick?: () => void;
  btnType?: 'submit' | 'button';
  noHover?: boolean;
  noBorder?: boolean;
  className?: string;
  label?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  icon?: React.ReactNode;
  typeText?: 'danger';
  type?: 'button' | 'submit' | 'reset';
  /**
   * Nome accessibile per i bottoni con la sola icona, che altrimenti uno
   * screen reader annuncia come "pulsante" e nient'altro.
   */
  ariaLabel?: string;
  /**
   * Selettore per i test e2e: nessuna delle prop sopra arriva al `<Button>`
   * Chakra sottostante come attributo `data-*`, va dichiarata a parte per
   * poterla raggiungere.
   */
  'data-testid'?: string;
}

const CustomButton: FC<ICustomButton> = ({
  color = 'primary',
  iconPosition = 'right',
  icon,
  size = 'md',
  outline,
  disabled,
  isLoading,
  text,
  textSmall,
  fullWidth,
  typeText,
  onClick,
  btnType = 'button',
  noHover,
  noBorder,
  children,
  className = '',
  type,
  ariaLabel,
  'data-testid': dataTestId,
}) => {
  const renderIcon = () => {
    if (!icon) return <Fragment />;
    if (typeof icon === 'string') {
      const iconsMap: Record<string, React.ReactNode> = {
        plus: (
          <PlusIcon
            className={`${
              iconPosition === 'left' ? 'me-2' : 'ms-2'
            } btn-icon btn-icon-size`}
          />
        ),
      };
      return iconsMap[icon];
    }
    // ReactNode (es. icona Lucide): wrap in span flex con margin verso il children
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: iconPosition === 'left' ? 6 : 0,
          marginLeft: iconPosition === 'right' ? 6 : 0,
        }}
      >
        {icon}
      </span>
    );
  };

  const renderChildren = () => {
    if (typeof children === 'string') {
      const fontSize =
        size === 'xs' ? 'bold-12' : size === 'sm' ? 'bold-12' : 'bold-12';
      return (
        <Typhography
          size={fontSize}
          className={`button-text${typeText ? `-${typeText}` : ''}`}
        >
          {children}
        </Typhography>
      );
    }
    return children;
  };

  const spinnerColor = {
    outline: '#00212c',
    primary: 'white',
    secondary: 'white',
    text: '#00212c',
    error: 'white',
  }[color];

  return (
    <Button
      type={type}
      loading={isLoading}
      spinner={<Spinner size={'xs'} color={spinnerColor} />}
      className={` ${objectToClassString({
        outline,
        [`bt-${color}`]: true,
        [`bt-size-${size}`]: size !== 'md',
        'btn-text': text,
        'btn-text-small': textSmall,
        'full-width': fullWidth,
        'no-hover': noHover,
        'btn-loading': isLoading,
        'no-border': noBorder,
      })} position-icon-${iconPosition} ${typeText} btn-custom ${className}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {/* {isLoading && (
        <div className="container__loader__button">
          <Spinner size={"xs"} className={`spinner-${color}`} />
        </div>
      )} */}
      <div className={classNames({ container__loading__button: isLoading })}>
        {icon ? (
          <div className="d-flex align-items-center">
            {iconPosition === 'left' && renderIcon()}
            {renderChildren()}
            {iconPosition === 'right' && renderIcon()}
          </div>
        ) : (
          renderChildren()
        )}
      </div>
    </Button>
  );
};

export default CustomButton;
